import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { Input, Icon, Pagination, Empty, Tooltip } from "antd";
import { getOrderStatus } from "../../../helpers/orders";

const PAGE_SIZE = 10;

const getOrderNumber = order => order.customer_order_number || "N/A";

const getHawb = order => {
  if (!order) return "N/A";
  const hawb = order.hawb || "N/A";
  return hawb.length > 12 ? `${hawb.slice(0, 12)}...` : hawb;
};

const getTotalAmount = (order, orderDetailsMap) => {
  const key = order.customer_order_id;
  const details = orderDetailsMap ? orderDetailsMap[key] : null;

  const amount =
    details && details.total_amount !== undefined && details.total_amount !== null
      ? details.total_amount
      : order.total_amount || order.balance_amount || 0;

  return Number(amount).toFixed(2);
};

const LeftOrdersPanel = ({
  invoice_orders = [],
  selectedOrderId = null,
  onOrderSelect = () => {},
  orderDetailsMap = {},
  onFilteredListChange = () => {}, //Added for after search showing 1st order detail from seached item
  orderDetails = {},
  rebillingProgress = false,
}) => {
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const filteredOrders = useMemo(() => {
    if (!invoice_orders || !Array.isArray(invoice_orders)) return [];
    if (!searchText) return invoice_orders;

    const search = searchText.trim().toLowerCase();
    return invoice_orders.filter(order => {
      const orderNumber = String(getOrderNumber(order)).toLowerCase();
      const hawb = String(order.hawb || "").toLowerCase();
      return orderNumber.indexOf(search) !== -1 || hawb.indexOf(search) !== -1;
    });
  }, [invoice_orders, searchText]);

  // Added for after search showing 1st order detail from seached item
  useEffect(() => {
    if (searchText && filteredOrders.length === 0) {
      // No matches for the current search — clear selection so right panel shows empty state
      onFilteredListChange(null);
      return;
    }

    if (filteredOrders.length > 0) {
      // Covers both: actively searching (show top match) and search cleared (show first of full list)
      onFilteredListChange(filteredOrders[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredOrders, searchText]);

  const handleSearchChange = e => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const totalCount = filteredOrders.length;
  const startIndex = (currentPage - 1) * PAGE_SIZE;

  const pagedOrders = useMemo(() => {
    return filteredOrders.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredOrders, startIndex]);

  const rangeStart = totalCount === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(startIndex + PAGE_SIZE, totalCount);

  return (
    <div className="left-orders-panel">
      <div className="left-orders-panel__header">
        <span className="left-orders-panel__title">ORDERS IN THIS INVOICE</span>
        <span className="left-orders-panel__count-badge">{totalCount}</span>
      </div>

      <div className="left-orders-panel__search">
        <Input
          placeholder="Search order number or HAWB"
          prefix={<Icon type="search" className="left-orders-panel__search-icon" />}
          value={searchText}
          onChange={handleSearchChange}
          allowClear
          disabled={rebillingProgress}
        />
      </div>

      <div className="left-orders-panel__list">
        {pagedOrders.length === 0 ? (
          <Empty description="No orders found" />
        ) : (
          pagedOrders.map(order => {
            const orderId = order.customer_order_id || "";
            const orderType = getOrderStatus(order.customer_order_type);
            const isSelected = orderId === selectedOrderId;
            const { total_amount, balance_amount, is_rebill_completed, is_file_generated } = order;

            // use orderDetailsMap keyed by this card's orderId instead of the single async orderDetails
            const detailsForThisOrder = orderDetailsMap ? orderDetailsMap[orderId] : null;
            const is_rebill_order = detailsForThisOrder?.is_rebill_order;

            const totalAmount = _.toNumber(total_amount);
            const balanceAmount = _.toNumber(balance_amount);

            const isCurrentOrderRebilled = is_rebill_order === true || is_rebill_order === "true";

            const isRebillCompleted =
              is_rebill_completed === true ||
              is_rebill_completed === "true" ||
              isCurrentOrderRebilled;
            const isFileGenerated = is_file_generated === true || is_file_generated === "true";
            const isOrderLocked =
              (totalAmount !== 0 && totalAmount !== balanceAmount) ||
              isRebillCompleted ||
              isFileGenerated;

            return (
              <div
                key={orderId}
                className={`order-card${isSelected ? " order-card--selected" : ""}`}
                onClick={() => onOrderSelect(order)}
              >
                <div className="order-card__accent" />
                <div className="order-card__body">
                  <div className="order-card__number">
                    <span>
                      {getOrderNumber(order)} /{" "}
                      {getHawb(order).length > 12 ? (
                        <Tooltip title={order.hawb || "N/A"}>
                          <span>{getHawb(order)}</span>
                        </Tooltip>
                      ) : (
                        getHawb(order)
                      )}
                    </span>
                    {isOrderLocked && (
                      <Icon type="lock" className="order-card__lock-icon" title="Order is locked" />
                    )}
                  </div>
                  {/* Clean text instead of an AntD Tag pill */}
                  <div className="order-card__type-text">{orderType}</div>
                </div>
                <div className="order-card__amount">${getTotalAmount(order, orderDetailsMap)}</div>
              </div>
            );
          })
        )}
      </div>

      {totalCount > 0 && (
        <div className="left-orders-panel__footer">
          <span className="left-orders-panel__range-text">
            {rangeStart} - {rangeEnd} of {totalCount}
          </span>
          <Pagination
            simple
            current={currentPage}
            pageSize={PAGE_SIZE}
            total={totalCount}
            onChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

LeftOrdersPanel.propTypes = {
  invoice_orders: PropTypes.array,
  selectedOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onOrderSelect: PropTypes.func,
  orderDetailsMap: PropTypes.func,
  onFilteredListChange: PropTypes.func,
  orderDetails: PropTypes.object,
  rebillingProgress: PropTypes.bool,
};

export default LeftOrdersPanel;
